import { CYCLE, TONES, IMAGES } from "@/data/content";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBanner } from "@/components/CtaBanner";
import { Eyebrow, SectionTitle } from "@/components/Section";
import { MethodologyExperience } from "@/components/MethodologyExperience";
import { Blob } from "@/components/Decor";

const PRINCIPLES = [
  { t: "Πρώτα κατανοούμε, μετά προτείνουμε", d: "Καμία πρόταση πριν από την ανάλυση της επιχείρησης και της αγοράς.", tone: "copper" },
  { t: "Ό,τι σχεδιάζεται, εφαρμόζεται", d: "Το πλάνο έχει υπευθύνους, χρονισμό και πραγματική υποστήριξη στην υλοποίηση.", tone: "slate" },
  { t: "Τίποτα δεν θεωρείται ολοκληρωμένο αν δεν μετριέται", d: "Κάθε παρέμβαση συνδέεται με δείκτες και κριτήρια επιτυχίας.", tone: "sage" },
  { t: "Η βελτίωση είναι κύκλος, όχι έργο", d: "Τα δεδομένα τροφοδοτούν τον επόμενο κύκλο ανάπτυξης.", tone: "gold" },
];

export default function Approach() {
  return (
    <div data-testid="approach-page">
      <PageHero
        testId="approach-hero"
        eyebrow="Μεθοδολογία"
        tone="#2C4A6F"
        title={
          <>
            Από την ανάλυση, στη <span className="italic text-[#2C4A6F]">συνεχή βελτίωση.</span>
          </>
        }
        desc="Η προσέγγισή μας είναι ένας δομημένος κύκλος έξι σταδίων. Δεν σταματάμε στη συμβουλή — προχωράμε μέχρι την εφαρμογή, τη μέτρηση και την προσαρμογή."
        aside={
          <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="approach-hero-steps">
            {CYCLE.map((c) => (
              <div key={c.step} className="min-w-0 rounded-2xl border border-[#0E1B31]/[0.07] bg-white/80 backdrop-blur px-2 py-4 text-center surface-hover sm:p-4">
                <span className="mx-auto flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-mono-brand text-white" style={{ background: TONES[c.tone].accent }}>{c.step}</span>
                <p className="mt-2 break-normal whitespace-nowrap font-display text-[13px] font-semibold text-[#0E1B31] sm:break-words sm:whitespace-normal sm:text-base">{c.title}</p>
              </div>
            ))}
          </div>
        }
      />

      <section className="relative py-16 lg:py-24 overflow-hidden" data-testid="methodology-section">
        <Blob color="rgba(44,74,111,0.12)" className="top-10 right-[-10%]" size={600} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <MethodologyExperience />
          </Reveal>
        </div>
      </section>

      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="principles-section">
        <img src={IMAGES.workspace} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-[#F8F6F0]/90 backdrop-blur-sm" />
        <div className="absolute inset-x-0 top-0 hairline" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow tone="#C5A059">Η Αρχή Πίσω Από Όλα</Eyebrow>
            <SectionTitle className="mt-6 lg:relative lg:-top-1 lg:-mb-6 lg:leading-[48px]">Ανάλυση → Στρατηγική → Σχεδιασμός → Εφαρμογή → <span className="italic text-[#C26D52]">Μέτρηση</span> → Βελτίωση</SectionTitle>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            {PRINCIPLES.map((p, i) => {
              const t = TONES[p.tone];
              return (
                <Reveal key={p.t} delay={i * 0.07}>
                  <div data-testid={`principle-card-${i}`} className="relative overflow-hidden rounded-[28px] glass p-8 surface-hover h-full">
                    <span className="absolute -right-3 -top-6 font-display text-[7rem] leading-none font-semibold select-none" style={{ color: `${t.accent}18` }}>0{i + 1}</span>
                    <span className="block h-1 w-14 rounded-full" style={{ background: t.accent }} />
                    <h3 className="mt-5 font-display text-2xl font-semibold text-[#0E1B31] leading-snug">{p.t}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#3B4A5E]">{p.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Δείτε πώς θα δούλευε αυτός ο κύκλος στη δική σας επιχείρηση."
        subtitle="Ξεκινάμε με μια δομημένη διάγνωση της τρέχουσας κατάστασης — χωρίς δεσμεύσεις."
        buttonLabel="Συζητήστε την επόμενη φάση ανάπτυξής σας"
      />
    </div>
  );
}
